import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import * as UserApi from "../../api/UserApi"
import { ErrorResponse, User, UserData } from "models"
import { useForm, useAlert, useAuth } from "hooks"

import { formStatusEnum } from "components/Form"
import { Spinner, Button, Modal, Markdown } from "components/Elements"
import { useBikesStore } from "features/dashboard/hooks"
import { publicRoutes } from "router/routes"

type UserDeleteAccountProps = {
	userData: Partial<User & UserData>
}

export const UserDeleteAccount: React.FC<UserDeleteAccountProps> = ({ userData }) => {
	const [isModalOpen, setModalIsOpen] = React.useState<boolean>(false)
	const { bikesStore, fetchBikes } = useBikesStore()
	const { formStatus, setFormStatus } = useForm({
		defaultFormStatus: formStatusEnum.ready,
	})
	const { t } = useTranslation()
	const { alert } = useAlert()
	const { logout } = useAuth()
	const navigate = useNavigate()

	const isBikeDisplayedOnDashboard = bikesStore?.bikes?.length > 0
	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	React.useEffect(() => {
		// We fetch bikes
		if (!bikesStore.bikes) fetchBikes()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()

		try {
			// prevent form to be submitted again
			setFormStatus(formStatusEnum.loading)

			const { getError } = await UserApi.deleteAccount(
				userData.account_id,
				userData.row_version
			)
			if (getError()) throw getError()

			alert("success", t("success_delete_account", { ns: "form" }))
			// redirect to login page
			logout()
			navigate(publicRoutes.signin.path)
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
			setFormStatus(formStatusEnum.ready)
		}
	}

	return (
		<>
			<div className="c-block px-4 lg:px-8 py-6 md:py-10 mt-8 md:mt-16">
				<h2 className="c-block__title text-2xl -top-6">
					{t("delete_account", { ns: "user" })}
				</h2>

				<p>{t("delete_account_description", { ns: "user" })}</p>

				<div className="mt-6">
					<Button
						variant="primary"
						type="submit"
						onClick={() => setModalIsOpen((prevIsModalOpen) => !prevIsModalOpen)}
						className="px-6 py-1"
						size="small"
						disabled={isBikeDisplayedOnDashboard}
					>
						{t("delete_account", { ns: "user" })}
					</Button>
				</div>
			</div>
			<Modal
				title={t("delete_account", { ns: "user" })}
				isOpen={isModalOpen}
				setIsOpen={setModalIsOpen}
				className={"max-w-2xl"}
			>
				<Markdown
					specificComponents={{
						p: ({ children }: any) => (
							<p className="text-center md:px-6 my-8">{children}</p>
						),
					}}
				>
					{t("delete_account_description_modal", {
						ns: "user",
					})}
				</Markdown>

				<form>
					{isReady && (
						<div className="flex justify-center">
							<Button
								onClick={handleSubmit}
								className="mx-2 md:px-20 px-10 py-1"
								variant="secondary"
							>
								{t("global.delete")}
							</Button>
							<Button
								onClick={() => setModalIsOpen(false)}
								className="mx-2 px-8 py-1 min-w-0"
								variant="secondaryBordered"
							>
								{t("global.cancel")}
							</Button>
						</div>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}
				</form>
			</Modal>
		</>
	)
}
